// /app/generate — Composition surface with live streaming variants.
//
// Layout per design/Flows.html Flow 02 + 2026-05-22 handoff restyle:
//   Left:  brief panel — brand badge, topic, platforms, post type, variants
//          slider, reference assets, voice strip, big "Üret" CTA
//   Right: variant stream — one card per (platform × variantIndex). Each
//          card animates through: shimmer → image-reveal → text-stream → done.
//
// Backend wiring (PRESERVED from prior implementation):
//   1. POST /v1/generations { brand_id, target_platforms, prompt, content_types,
//                             post_type, variants_per_platform }
//      → 202 { generation: {id, status:"running"}, versions: [seeded lanes] }
//   2. Open WS /ws/generations/:id via useGenerationStream
//   3. Each gen.variant.* frame mutates a lane in the reducer
//   4. In parallel, poll GET /generations/:id until status is terminal —
//      the WS has no replay, so the poll is what guarantees convergence
//   5. Operator approves variants → POST /variants/:id/accept

import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, ApiError } from "@/lib/api";
import { resolveImageUrl } from "@/lib/asset-url";
import { GRAPHIC_MODELS, DESIGN_STYLES } from "@/lib/image-options";
import { useEngineHealth, maintenanceSuffix } from "@/hooks/use-engine-health";
import { BulkWizard } from "@/routes/app/bulk";
import { useBrands } from "@/hooks/use-persona";
import { useActiveBrand } from "@/stores/active-brand";
import { useGenerationStream, type VariantLane } from "@/hooks/use-generation-stream";
import { Icons, PlatformIcon, Rosette } from "@/components/icons";
import {
  BrandMark,
  PageHead,
  PlatformChip,
  Skeleton,
  Toggle,
  commentsDisabledHint,
  ImageFailureNotice,
  imageFailure,
} from "@/components/primitives";
import type { Brand, Platform } from "@/lib/types";

export const Route = createFileRoute("/app/generate")({
  component: GeneratePage,
});

const ALL_PLATFORMS: Platform[] = ["instagram", "facebook", "x", "linkedin", "telegram"];

// Subset of GenerationVersion that the synchronous POST /generations
// returns. Used as a fallback data source when the WS stream missed
// the events (the work runs server-side BEFORE the WS subscribes, so
// real-time events fire too early to be seen).
export interface SeededVersion {
  id: string;
  version_number: number;
  platform: Platform;
  provider?: string;
  status: string;
  text_body?: string | null;
  hashtags?: string[] | null;
  image_url?: string | null;
  text_model?: string;
  image_model?: string;
  /**
   * Additive failure reason for the image leg (content-service). Absent on
   * older rows and on every successful generation — the lane then renders
   * exactly as it always has.
   *   image_error_kind: safety_blocked | quota_exhausted | provider_error
   */
  image_error?: string | null;
  image_error_kind?: string | null;
}

const PLATFORM_META: Record<Platform, { name: string; color: string }> = {
  instagram: { name: "Instagram", color: "#E1306C" },
  facebook: { name: "Facebook", color: "#1877F2" },
  x: { name: "X", color: "#FFFFFF" },
  linkedin: { name: "LinkedIn", color: "#0A66C2" },
  telegram: { name: "Telegram", color: "#26A5E4" },
};

// Tek / Toplu segmented toggle at the top of İçerik Üret. Switches the whole
// screen between single-post generation and the embedded bulk planner. v0.1.x.
function GenModeToggle({
  mode,
  setMode,
  tr,
}: {
  mode: "single" | "bulk";
  setMode: (m: "single" | "bulk") => void;
  tr: boolean;
}) {
  const opts: Array<["single" | "bulk", string]> = [
    ["single", tr ? "Tek gönderi" : "Single post"],
    ["bulk", tr ? "Toplu" : "Bulk"],
  ];
  return (
    <div
      style={{
        display: "inline-flex",
        background: "var(--surface)",
        border: "1px solid var(--b2)",
        borderRadius: 8,
        padding: 3,
        gap: 2,
      }}
    >
      {opts.map(([id, label]) => (
        <button
          key={id}
          type="button"
          onClick={() => setMode(id)}
          style={{
            padding: "6px 16px",
            borderRadius: 6,
            fontSize: 13,
            fontWeight: mode === id ? 600 : 500,
            background: mode === id ? "var(--elev)" : "transparent",
            color: mode === id ? "var(--tx)" : "var(--tx2)",
            border: mode === id ? "1px solid var(--b3)" : "1px solid transparent",
            cursor: "pointer",
          }}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function GeneratePage() {
  const { i18n } = useTranslation("common");
  const tr = i18n.resolvedLanguage === "tr";

  const { data: brandsData } = useBrands();
  const brands = brandsData?.data ?? [];
  const activeBrand = useActiveBrand((s) => s.activeBrand);

  const nav = useNavigate();
  const [brandId, setBrandId] = useState<string>(activeBrand?.id ?? "");
  const brand = brands.find((b) => b.id === brandId) ?? activeBrand ?? brands[0] ?? null;

  // Per-brand connected accounts — gates the platform chips below.
  // brand.connected_platforms field isn't reliably populated yet so
  // we fetch /v1/brands/:id/social-accounts directly. Operator
  // (2026-05-23): "only enable integrated platforms to select".
  type SocialAccount = { platform: Platform; status: string };
  const { data: socialRes } = useQuery({
    queryKey: ["brand", brand?.id, "social-accounts"],
    queryFn: () =>
      api<{ data?: SocialAccount[]; social_accounts?: SocialAccount[] }>(
        `/brands/${brand?.id}/social-accounts`,
      ),
    enabled: !!brand?.id,
    staleTime: 60_000,
  });
  const connectedPlatforms = useMemo(() => {
    const accts = socialRes?.data ?? socialRes?.social_accounts ?? [];
    // Only count active accounts as connected — expired/revoked
    // shouldn't pre-select. Operator can reconnect from Integrations.
    const set = new Set<Platform>(
      accts
        .filter((a: SocialAccount) => a.status === "active")
        .map((a: SocialAccount) => a.platform),
    );
    // Telegram connects via brand.telegram_channel (shared @nexus_dijital_bot),
    // NOT an OAuth social account — so it never appears in /social-accounts.
    // A set channel means Telegram IS connected → enable the chip. Bugfix
    // 2026-06-29: operators connected Telegram but the chip stayed disabled.
    if (brand?.telegram_channel && brand.telegram_channel.trim() !== "") {
      set.add("telegram");
    }
    return set;
  }, [socialRes, brand?.telegram_channel]);

  // Start empty — no pre-filled example text. The textarea placeholder
  // guides the operator instead. (Pre-filling a sample brief meant every
  // brand saw the same coffee example on entry.)
  const [prompt, setPrompt] = useState("");
  // Default selection is empty — populated by the effect below when
  // /v1/brands/:id/social-accounts returns. This way the user never
  // sees pre-selected platforms they haven't actually connected.
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [postType, setPostType] = useState<"feed" | "story">("feed");
  // image_type — defaults to text_based (existing Claude-HTML typography
  // composer; pixel-perfect brand colors + embedded logo). 'graphical'
  // routes to fal.ai diffusion (GPT-Image primary, Nano Banana on
  // regenerate) for photoreal / cinematic / illustrative scenes that
  // Claude-HTML can't paint.
  const [imageType, setImageType] = useState<"text_based" | "graphical">("text_based");
  // v0.1.x — operator picks: graphical model (Grafik) or design style (Tipografi).
  // '' = Otomatik. Sent as image_engine / design_style; ignored server-side for
  // the non-matching image type.
  const [imageEngine, setImageEngine] = useState("");
  const [designStyle, setDesignStyle] = useState("");
  // Provider maintenance (v0.1.119) — engines whose AI provider account is
  // cut off render disabled. Empty until the health endpoint answers, so a
  // slow/failed read never greys out a working model.
  const engineHealth = useEngineHealth();
  // Set when we had to move the operator off a model that went into
  // maintenance, so the swap is explained instead of silently happening.
  const [engineFellBack, setEngineFellBack] = useState("");
  useEffect(() => {
    if (!imageEngine || !engineHealth.unavailable.has(imageEngine)) return;
    // Never leave a disabled engine selected. '' = Otomatik, which is
    // always safe: the backend roster picks a healthy engine itself.
    const replacement =
      GRAPHIC_MODELS.find(
        (m) => m.key !== "" && m.key !== imageEngine && !engineHealth.unavailable.has(m.key),
      )?.key ?? "";
    const was = GRAPHIC_MODELS.find((m) => m.key === imageEngine);
    setEngineFellBack(was ? (tr ? was.tr : was.en) : imageEngine);
    setImageEngine(replacement);
  }, [imageEngine, engineHealth.unavailable, tr]);
  // v0.1.x — İçerik Üret now hosts BOTH single and bulk generation behind a
  // Tek/Toplu toggle (Bulk is no longer a separate menu item). "bulk" renders
  // the embedded BulkWizard; "single" keeps the existing one-post flow.
  const [genMode, setGenMode] = useState<"single" | "bulk">("single");
  // imagePrompt — operator-visible diffusion prompt for graphical mode.
  // Auto-fills via /v1/generations/preview-image-prompt when the topic
  // changes (debounced); operator can edit before pressing Generate.
  // Sent verbatim as createGenReq.image_prompt; backend uses it as the
  // diffusion prompt instead of running the Haiku enricher. v0.1.63.
  const [imagePrompt, setImagePrompt] = useState("");
  const [imagePromptDirty, setImagePromptDirty] = useState(false);
  const [imagePromptLoading, setImagePromptLoading] = useState(false);
  // Non-empty when the auto-compose call failed; rendered under the
  // textarea so a failure is visible instead of silently disabling Generate.
  const [imagePromptError, setImagePromptError] = useState("");
  const [variantsPerPlatform, setVariantsPerPlatform] = useState(2);
  // v0.1.114 — publish the approved posts with comments/replies closed.
  // Only x + instagram can honour it; see commentsDisabledHint.
  const [disableComments, setDisableComments] = useState(false);

  const [activeGenId, setActiveGenId] = useState<string | null>(null);
  const stream = useGenerationStream(activeGenId);

  // Quota-exceeded modal — populated when content-service returns
  // 402 Payment Required from /v1/generations (Phase 5b enforcement).
  // The body shape is the QuotaState struct billing-service computed:
  //   {plan_slug, used, allowance, remaining, blocked_reason, requested}
  const [quotaBlock, setQuotaBlock] = useState<{
    plan_slug?: string;
    used?: number;
    allowance?: number;
    remaining?: number;
    blocked_reason?: string;
    requested?: number;
  } | null>(null);

  // v0.1.113 — POST /generations is ASYNC on the backend (content-service
  // v0.3.43): it answers 202 in milliseconds with the generation id plus a
  // SEEDED placeholder lane per (platform × variant), and the real work runs
  // in a background worker. Previously it ran the whole fan-out inline, which
  // blew past Cloudflare's ~100s edge budget on 4-platform text+image
  // generations — the browser got an aborted connection and the operator saw
  // nothing at all.
  //
  // Variant content now arrives two ways, and we use BOTH:
  //   1. the gen.variant.* WebSocket stream (live, per-lane, instant), and
  //   2. a poll of GET /generations/:id (below) — because nexus-stream is
  //      plain fire-and-forget NATS with no replay, so any frame emitted
  //      before the socket opened (or while the tab slept) is simply gone.
  // The poll is what guarantees the operator always ends up seeing results.
  const [seededVersions, setSeededVersions] = useState<SeededVersion[]>([]);
  // Stable across retries/double-clicks of the SAME generation attempt;
  // rotated once the server accepts one. See idempotency_key below.
  const idempotencyRef = useRef(crypto.randomUUID());
  const start = useMutation({
    mutationFn: () =>
      api<{ generation: { id: string }; versions: SeededVersion[] }>("/generations", {
        method: "POST",
        body: {
          brand_id: brandId || brand?.id,
          target_platforms: platforms,
          content_types: ["text", "image"],
          post_type: postType,
          image_type: imageType,
          // Only send image_prompt when graphical mode + operator
          // accepted/edited the auto-generated prompt; backend uses
          // it verbatim, else runs the Haiku enricher per-platform.
          image_prompt: imageType === "graphical" ? imagePrompt.trim() : "",
          // v0.1.x — graphical model pick (only for Grafik) + design style
          // (only for Tipografi). Backend ignores the irrelevant one.
          image_engine: imageType === "graphical" ? imageEngine : "",
          design_style: imageType === "text_based" ? designStyle : "",
          prompt: prompt.trim(),
          variants_per_platform: variantsPerPlatform,
          comments_disabled: disableComments,
          // content-service reads idempotency from the BODY
          // (createGenReq.idempotency_key) and never looks at the
          // Idempotency-Key header, so the header this used to send was
          // inert — a double-click created two generations and burned two
          // sets of credits. The key is also held in a ref rather than
          // minted per call: a fresh UUID every attempt can never dedup
          // anything. It rotates only after a generation actually starts.
          idempotency_key: idempotencyRef.current,
        },
      }),
    onSuccess: (r) => {
      setSeededVersions(r.versions ?? []);
      setActiveGenId(r.generation?.id ?? null);
      idempotencyRef.current = crypto.randomUUID(); // next run is a new one
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 402) {
        setQuotaBlock((err.body as typeof quotaBlock) ?? {});
      }
    },
  });

  // Poll the generation until it reaches a terminal status. Belt to the
  // WebSocket's braces (see the seededVersions comment): the async backend
  // can finish a lane while the socket is still connecting, so this is the
  // only source that's guaranteed to converge. Stops polling — and stops
  // overwriting locally-appended regenerated variants — once the row is
  // ready/failed/approved.
  const genPoll = useQuery({
    queryKey: ["generation", activeGenId],
    enabled: !!activeGenId,
    queryFn: () =>
      api<{ generation: { id: string; status: string; post_type?: string }; versions: SeededVersion[] }>(
        `/generations/${activeGenId}`,
      ),
    refetchInterval: (q) => {
      const s = q.state.data?.generation?.status;
      return s === "ready" || s === "failed" || s === "approved" ? false : 4000;
    },
  });
  const genStatus = genPoll.data?.generation?.status;

  useEffect(() => {
    const vs = genPoll.data?.versions;
    // Only replace the placeholder lanes once real rows exist — an empty
    // versions[] mid-run must not wipe the shimmer placeholders.
    if (vs && vs.length > 0) setSeededVersions(vs);
  }, [genPoll.data]);

  const canStart =
    !!(brandId || brand?.id) &&
    platforms.length > 0 &&
    prompt.trim().length >= 4 &&
    // In graphical mode, wait for the AI-composed image prompt to finish
    // (and be non-empty) before allowing Generate — otherwise an early click
    // ships a blank/half-built image prompt. The operator can still edit the
    // textarea before generating.
    !(imageType === "graphical" && (imagePromptLoading || imagePrompt.trim().length === 0)) &&
    !start.isPending;

  const totalVariants = platforms.length * variantsPerPlatform;

  // Auto-select all connected platforms when the brand or social-
  // accounts list changes. This way the operator lands on a sensible
  // default (everything they've connected) but can still deselect.
  useEffect(() => {
    if (connectedPlatforms.size === 0) {
      setPlatforms([]);
      return;
    }
    setPlatforms((prev) => {
      // Keep prev selections that are still connected; drop any that
      // got disconnected since last render.
      const next = prev.filter((p) => connectedPlatforms.has(p));
      if (next.length > 0) return next;
      // Nothing selected — default to all connected.
      return Array.from(connectedPlatforms) as Platform[];
    });
  }, [connectedPlatforms]);

  // v0.1.125 — story mode accepts EVERY connected platform. IG + FB get a
  // true 9:16 Story; the backend resolves the rest to their normal feed
  // shape (X 16:9, Telegram square, LinkedIn landscape) and the normal
  // feed publish path. The v0.1.64 effect that pruned non-Meta platforms
  // here is gone — the 400 it dodged no longer exists (content v0.3.66).

  // Stable string form of the first selected platform. The enricher only
  // needs this hint, and depending on a string (rather than the `platforms`
  // array, whose identity churns on every social-accounts refetch) keeps the
  // debounce timer below from being cancelled on unrelated re-renders.
  const platformHint = platforms[0] || "instagram";

  // Debounced auto-fill of the image-prompt textarea when in graphical
  // mode. Fires 700ms after the operator stops typing the topic so
  // we don't spam Haiku on every keystroke. Skipped when:
  //   - mode is text_based (no diffusion involved)
  //   - operator has edited the textarea (imagePromptDirty) — respect
  //     their override
  //   - topic too short to enrich meaningfully (<4 chars matches the
  //     same threshold canStart uses)
  // The first platform from `platforms` is sent as a hint so the
  // enricher knows whether to bias towards square/landscape composition.
  useEffect(() => {
    if (imageType !== "graphical") return;
    if (imagePromptDirty) return;
    const topic = prompt.trim();
    if (topic.length < 4) {
      setImagePrompt("");
      return;
    }
    const bid = brandId || brand?.id;
    if (!bid) return;
    const t = window.setTimeout(async () => {
      setImagePromptLoading(true);
      setImagePromptError("");
      try {
        const r = await api<{ image_prompt: string }>(
          "/generations/preview-image-prompt",
          {
            method: "POST",
            body: {
              brand_id: bid,
              platform: platformHint,
              post_type: postType,
              prompt: topic,
              // The pre-filled prompt must suit the engine that will
              // render it: fal edit models render legible copy + the
              // logo, "Nexus AI" wants a BACKDROP only (our own layer
              // draws the text). Without this the textarea was always
              // filled with a text-inclusive branded prompt, which then
              // won over Nexus AI's art direction and made Stability
              // paint garbled lettering under our clean text.
              image_engine: imageEngine,
            },
          },
        );
        // Don't clobber if the operator started editing during the
        // 700ms debounce window.
        if (!imagePromptDirty) {
          setImagePrompt(r.image_prompt || "");
        }
      } catch (e) {
        // 2026-07-26 — this used to swallow the error silently. Combined
        // with canStart requiring a non-empty imagePrompt, a failed call
        // left Generate permanently disabled with NO explanation on screen.
        // Surface it so the operator knows to type a prompt by hand.
        setImagePromptError(
          e instanceof Error ? e.message : "could not compose an image prompt",
        );
      } finally {
        setImagePromptLoading(false);
      }
    }, 700);
    return () => window.clearTimeout(t);
    // platformHint (a string) — NOT the `platforms` array. The array gets a
    // fresh identity every time the social-accounts query refetches (window
    // focus ⇒ new connectedPlatforms Set ⇒ the auto-select effect calls
    // setPlatforms(prev => prev.filter(...)), which always allocates). That
    // re-ran this effect and cleared the 700ms timer, so on an unlucky focus
    // rhythm the prompt never composed and Generate stayed disabled.
    // imageEngine is a dependency because the prompt SHAPE depends on it —
    // switching Grafik modeli to/from Nexus AI must re-compose the prompt
    // (unless the operator has already edited it, which still wins).
  }, [imageType, prompt, postType, platformHint, brandId, brand?.id, imagePromptDirty, imageEngine]);

  // Bulk mode → embed the full BulkWizard under the shared mode toggle.
  if (genMode === "bulk") {
    return (
      <div className="generate-root" style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <div style={{ padding: "16px 20px 0" }}>
          <GenModeToggle mode={genMode} setMode={setGenMode} tr={tr} />
        </div>
        <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          <BulkWizard />
        </div>
      </div>
    );
  }

  return (
    <div className="generate-root" style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ padding: "16px 20px 0" }}>
        <GenModeToggle mode={genMode} setMode={setGenMode} tr={tr} />
      </div>
      <PageHead
        eyebrow={tr ? `03 · Üret${brand ? ` · ${brand.trade_name}` : ""}` : `03 · Generate${brand ? ` · ${brand.trade_name}` : ""}`}
        title={tr ? "Bir konu gir. Beş platform için içerik çıksın." : "One topic in. Five platforms out."}
        subtitle={
          tr
            ? "Varyantlar canlı akar. Onayla, düzenle veya yeniden üret. Sen demediğin sürece hiçbiri yayına gitmez."
            : "Stream variants in real time. Approve, edit, or regenerate any one. Nothing leaves this surface until you say so."
        }
      />

      <div
        className="generate-split"
        style={{
          flex: 1,
          display: "grid",
          gridTemplateColumns: "380px 1fr",
          overflow: "hidden",
        }}
      >
        {/* Left brief panel */}
        <div
          style={{
            borderRight: "1px solid var(--b1)",
            padding: 24,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 18,
          }}
        >
          {/* Brand picker */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: 12,
              background: "var(--surface)",
              border: "1px solid var(--b2)",
              borderRadius: 10,
            }}
          >
            {brand ? <BrandMark brand={brand} size={36} /> : null}
            <div style={{ flex: 1, minWidth: 0 }}>
              <select
                value={brandId || brand?.id || ""}
                onChange={(e) => setBrandId(e.target.value)}
                className="input"
                style={{ width: "100%", fontSize: 13, fontWeight: 600, padding: "6px 8px" }}
              >
                <option value="">{tr ? "Marka seç" : "Pick a brand"}</option>
                {brands.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.trade_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Topic */}
          <div>
            <label
              className="mono"
              style={{
                fontSize: 10,
                color: "var(--mu)",
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginBottom: 8,
              }}
            >
              <Icons.Edit size={11} /> {tr ? "Konu" : "Topic"}
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              className="input"
              rows={5}
              placeholder={
                tr
                  ? "Ne hakkında paylaşım üretelim? Konuyu veya kısa brief'i yaz."
                  : "What should we post about? Write the topic or a short brief."
              }
              style={{ resize: "vertical", lineHeight: 1.6, fontSize: 13, width: "100%" }}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: 6,
              }}
            >
              <span className="mono" style={{ fontSize: 10, color: "var(--mu)" }}>
                {prompt.length} {tr ? "karakter" : "chars"}
              </span>
            </div>
          </div>

          {/* Platforms */}
          <div>
            <label
              className="mono"
              style={{
                fontSize: 10,
                color: "var(--mu)",
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                display: "block",
                marginBottom: 8,
              }}
            >
              {tr ? "Platformlar" : "Platforms"}
            </label>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {ALL_PLATFORMS.map((pl) => {
                const active = platforms.includes(pl);
                // Only platforms with an ACTIVE social account are
                // selectable. Connect missing ones from /app/integrations
                // or the brand cockpit Accounts tab first.
                const connected = connectedPlatforms.has(pl);
                // v0.1.125 — story mode no longer disables non-Meta chips.
                // Platforms without a story API are selectable and publish
                // as normal FEED posts (backend effectivePostType); the
                // chip carries a small "feed" hint so the operator knows.
                const storyAsFeed =
                  postType === "story" && pl !== "instagram" && pl !== "facebook";
                const meta = PLATFORM_META[pl];
                return (
                  <button
                    key={pl}
                    type="button"
                    disabled={!connected}
                    title={
                      storyAsFeed && connected
                        ? tr
                          ? `${meta.name} hikayeleri desteklemiyor — akış gönderisi olarak paylaşılır.`
                          : `${meta.name} doesn't support Stories — will be posted as a feed post.`
                        : connected
                          ? meta.name
                          : tr
                            ? `${meta.name} hesabını henüz bağlamadın. Bağlamak için Hesaplar sekmesine git.`
                            : `${meta.name} not connected — open the Accounts tab to link it first.`
                    }
                    onClick={() =>
                      setPlatforms((p) =>
                        p.includes(pl) ? p.filter((x) => x !== pl) : [...p, pl],
                      )
                    }
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "6px 10px",
                      borderRadius: 6,
                      fontSize: 12,
                      background: active ? `${meta.color}1a` : "var(--surface)",
                      border: `1px solid ${active ? meta.color + "55" : "var(--b2)"}`,
                      color: !connected
                        ? "var(--mu)"
                        : active
                          ? meta.color
                          : "var(--tx2)",
                      opacity: !connected ? 0.4 : 1,
                      cursor: !connected ? "not-allowed" : "pointer",
                    }}
                  >
                    <PlatformIcon platform={pl} size={12} />
                    {meta.name}
                    {!connected ? <Icons.Lock size={10} /> : null}
                    {storyAsFeed && connected ? (
                      <span
                        className="mono"
                        style={{
                          fontSize: 9,
                          padding: "1px 5px",
                          borderRadius: 4,
                          background: "var(--elev)",
                          border: "1px solid var(--b2)",
                          color: "var(--mu)",
                          letterSpacing: "0.04em",
                        }}
                      >
                        {tr ? "Akış" : "Feed"}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
            {/* v0.1.125 — story mode + non-story platform selected: explain
                that those lanes publish as ordinary feed posts. */}
            {postType === "story" &&
            platforms.some((p) => p !== "instagram" && p !== "facebook") ? (
              <div
                style={{
                  marginTop: 8,
                  fontSize: 11,
                  color: "var(--tx2)",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <Icons.Info size={12} style={{ color: "var(--mu)", flexShrink: 0 }} />
                {tr
                  ? "Hikayeler yalnızca Instagram + Facebook'ta yayınlanır; diğer seçili platformlarda içerik akış gönderisi olarak paylaşılır."
                  : "Stories publish on Instagram + Facebook only; on the other selected platforms the content will be posted as a feed post."}
              </div>
            ) : null}
            {/* Empty-state CTA when no platforms connected on this brand */}
            {connectedPlatforms.size === 0 && brand?.id ? (
              <div
                style={{
                  marginTop: 8,
                  padding: "10px 12px",
                  background: "var(--surface)",
                  border: "1px solid var(--b2)",
                  borderRadius: 6,
                  fontSize: 12,
                  color: "var(--tx2)",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Icons.Info size={13} style={{ color: "var(--warning)", flexShrink: 0 }} />
                <span style={{ flex: 1 }}>
                  {tr
                    ? "Bu markada henüz bağlı sosyal hesap yok. Üretmeden önce en az birini bağla."
                    : "No connected social accounts on this brand. Connect at least one before generating."}
                </span>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() =>
                    nav({
                      to: "/app/brands/$brandId",
                      params: { brandId: brand.id },
                      search: { tab: "accounts" } as Record<string, string>,
                    })
                  }
                >
                  {tr ? "Hesapları bağla →" : "Connect accounts →"}
                </button>
              </div>
            ) : null}
          </div>

          {/* Post type */}
          <div>
            <label
              className="mono"
              style={{
                fontSize: 10,
                color: "var(--mu)",
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                display: "block",
                marginBottom: 8,
              }}
            >
              {tr ? "Gönderi türü" : "Post type"}
            </label>
            <div
              style={{
                display: "flex",
                background: "var(--surface)",
                border: "1px solid var(--b2)",
                borderRadius: 8,
                padding: 3,
              }}
            >
              {(
                [
                  ["feed", tr ? "Akış" : "Feed", tr ? "Görsel · Metin" : "Image · Text"],
                  ["story", tr ? "Hikaye" : "Story", tr ? "Dikey · 24sa" : "Vertical · 24h"],
                ] as const
              ).map(([id, label, sub]) => (
                <button
                  key={id}
                  onClick={() => setPostType(id)}
                  style={{
                    flex: 1,
                    padding: "8px 10px",
                    borderRadius: 6,
                    fontSize: 12,
                    background: postType === id ? "var(--elev)" : "transparent",
                    color: postType === id ? "var(--tx)" : "var(--tx2)",
                    border:
                      postType === id ? "1px solid var(--b3)" : "1px solid transparent",
                    fontWeight: postType === id ? 600 : 500,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <span>{label}</span>
                  <span
                    style={{ fontSize: 10, color: "var(--mu)", fontFamily: "var(--mono)" }}
                  >
                    {sub}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Image type chip — text_based (Claude-HTML typography) vs
              graphical (fal.ai diffusion). Default text_based. Same
              two-chip pattern as Post type above for visual consistency. */}
          <div>
            <label
              className="mono"
              style={{
                fontSize: 10,
                color: "var(--mu)",
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                display: "block",
                marginBottom: 8,
              }}
            >
              {tr ? "Görsel türü" : "Image type"}
            </label>
            <div
              style={{
                display: "flex",
                background: "var(--surface)",
                border: "1px solid var(--b2)",
                borderRadius: 8,
                padding: 3,
              }}
            >
              {(
                [
                  [
                    "text_based",
                    tr ? "Tipografi" : "Text-based",
                    tr ? "Marka renkleri · Logo" : "Brand colors · Logo",
                  ],
                  [
                    "graphical",
                    tr ? "Grafik" : "Graphical",
                    tr ? "Fotoğrafik · Sinematik" : "Photoreal · Cinematic",
                  ],
                ] as const
              ).map(([id, label, sub]) => (
                <button
                  key={id}
                  onClick={() => setImageType(id)}
                  style={{
                    flex: 1,
                    padding: "8px 10px",
                    borderRadius: 6,
                    fontSize: 12,
                    background: imageType === id ? "var(--elev)" : "transparent",
                    color: imageType === id ? "var(--tx)" : "var(--tx2)",
                    border:
                      imageType === id ? "1px solid var(--b3)" : "1px solid transparent",
                    fontWeight: imageType === id ? 600 : 500,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <span>{label}</span>
                  <span
                    style={{ fontSize: 10, color: "var(--mu)", fontFamily: "var(--mono)" }}
                  >
                    {sub}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* v0.1.x — model / style picker under the image-type toggle.
              Grafik → which fal model (preferred, with server fallback);
              Tipografi → which high-quality design layout. */}
          {imageType === "graphical" ? (
            <div>
              <label
                className="mono"
                style={{ fontSize: 10, color: "var(--mu)", letterSpacing: "0.15em", textTransform: "uppercase", display: "block", marginBottom: 8 }}
              >
                {tr ? "Grafik modeli" : "Graphic model"}
              </label>
              <select
                value={imageEngine}
                onChange={(e) => {
                  setEngineFellBack("");
                  setImageEngine(e.target.value);
                }}
                style={{ width: "100%", padding: "8px 10px", borderRadius: 8, fontSize: 13, background: "var(--surface)", border: "1px solid var(--b2)", color: "var(--tx)" }}
              >
                {GRAPHIC_MODELS.map((m) => {
                  const down = engineHealth.unavailable.has(m.key);
                  const note = tr ? m.note_tr : m.note_en;
                  // Under maintenance replaces the marketing note — the
                  // operator needs the blocker, not the speed claim.
                  const suffix = down ? maintenanceSuffix(tr) : note ? " — " + note : "";
                  return (
                    <option key={m.key} value={m.key} disabled={down}>
                      {(tr ? m.tr : m.en) + suffix}
                    </option>
                  );
                })}
              </select>
              {engineFellBack ? (
                <div style={{ fontSize: 11, color: "var(--mu)", marginTop: 6, lineHeight: 1.5 }}>
                  {tr
                    ? `${engineFellBack} şu anda bakımda — sağlayıcı hesabı yanıt vermiyor. Kullanılabilir bir modele geçildi.`
                    : `${engineFellBack} is under maintenance — its provider account isn't responding. Switched to an available model.`}
                </div>
              ) : null}
            </div>
          ) : (
            <div>
              <label
                className="mono"
                style={{ fontSize: 10, color: "var(--mu)", letterSpacing: "0.15em", textTransform: "uppercase", display: "block", marginBottom: 8 }}
              >
                {tr ? "Tasarım stili" : "Design style"}
              </label>
              <select
                value={designStyle}
                onChange={(e) => setDesignStyle(e.target.value)}
                style={{ width: "100%", padding: "8px 10px", borderRadius: 8, fontSize: 13, background: "var(--surface)", border: "1px solid var(--b2)", color: "var(--tx)" }}
              >
                {DESIGN_STYLES.map((s) => (
                  <option key={s.key} value={s.key}>
                    {tr ? s.tr : s.en}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Editable image prompt — only when graphical mode.
              Auto-fills via Haiku enricher (debounced on topic change);
              operator can edit before pressing Generate. v0.1.63. */}
          {imageType === "graphical" ? (
            <div>
              <label
                className="mono"
                style={{
                  fontSize: 10,
                  color: "var(--mu)",
                  letterSpacing: "0.15em",
                  textTransform: "uppercase",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 8,
                }}
              >
                <span>{tr ? "Görsel istemi (düzenlenebilir)" : "Image prompt (editable)"}</span>
                {imagePromptLoading ? (
                  <span style={{ fontSize: 9, color: "var(--mu)" }}>
                    {tr ? "hazırlanıyor…" : "preparing…"}
                  </span>
                ) : null}
              </label>
              <textarea
                className="input"
                value={imagePrompt}
                onChange={(e) => {
                  setImagePrompt(e.target.value);
                  setImagePromptDirty(true);
                }}
                placeholder={
                  tr
                    ? "Konuyu yaz, AI burayı vurgulu bir İngilizce görsel istemiyle dolduracak — istersen düzenle."
                    : "Type the topic; AI will fill this with a vivid English diffusion prompt — edit if needed."
                }
                rows={4}
                style={{ width: "100%", resize: "vertical", fontSize: 12, lineHeight: 1.5 }}
              />
              {imagePromptError && !imagePrompt.trim() ? (
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--error)",
                    marginTop: 6,
                    lineHeight: 1.45,
                  }}
                >
                  {tr
                    ? `Görsel istemi otomatik hazırlanamadı (${imagePromptError}). Yukarıya kendin bir istem yazarak devam edebilirsin.`
                    : `Couldn't compose the image prompt automatically (${imagePromptError}). Type one above to continue.`}
                </div>
              ) : null}
              <div
                style={{
                  fontSize: 10,
                  color: "var(--mu)",
                  marginTop: 4,
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <span>
                  {tr
                    ? "İpucu — İngilizce daha iyi sonuç verir. Konu, ışık, kompozisyon belirt."
                    : "Tip — English yields better results. Specify subject, lighting, composition."}
                </span>
                {imagePromptDirty ? (
                  <button
                    onClick={() => {
                      setImagePromptDirty(false);
                      setImagePrompt("");
                    }}
                    style={{
                      background: "none",
                      border: 0,
                      color: "var(--accent)",
                      fontFamily: "var(--mono)",
                      fontSize: 10,
                      cursor: "pointer",
                      padding: 0,
                    }}
                  >
                    ↺ {tr ? "yeniden öner" : "regenerate"}
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}

          {/* v0.1.114 — close the comment/reply section on the posts
              approved from this generation. Honoured on X + Instagram
              only; the hint names which of the SELECTED platforms will
              actually apply it. */}
          <div>
            <Toggle
              on={disableComments}
              onChange={setDisableComments}
              label={tr ? "Yorumları kapat" : "Disable comments"}
            />
            <div style={{ fontSize: 10, color: "var(--mu)", marginTop: 4 }}>
              {commentsDisabledHint(platforms, tr)}
            </div>
          </div>

          {/* Variants slider */}
          <div>
            <label
              className="mono"
              style={{
                fontSize: 10,
                color: "var(--mu)",
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <span>{tr ? "Platform başına varyant" : "Variants per platform"}</span>
              <span style={{ color: "var(--accent)" }}>{variantsPerPlatform}</span>
            </label>
            <input
              type="range"
              min={1}
              max={4}
              value={variantsPerPlatform}
              onChange={(e) => setVariantsPerPlatform(Number(e.target.value))}
              style={{ width: "100%", accentColor: "var(--accent)" }}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 10,
                color: "var(--mu)",
                fontFamily: "var(--mono)",
                marginTop: 4,
              }}
            >
              <span>1 · {tr ? "hızlı" : "fast"}</span>
              <span>4 · {tr ? "kapsamlı" : "thorough"}</span>
            </div>
          </div>

          {/* Surface ANY error, not just ApiError — network failures,
              CORS, JSON parse errors etc. were previously swallowed,
              leaving the button looking like it did nothing on click. */}
          {start.isError ? (
            <p style={{ color: "var(--error)", fontSize: 12 }} role="alert">
              {start.error instanceof ApiError
                ? start.error.message
                : (start.error as Error)?.message || "Unknown error"}
            </p>
          ) : null}
          {start.isPending ? (
            <p style={{ color: "var(--mu)", fontSize: 12 }}>
              {tr ? "Üretim başlatılıyor…" : "Starting generation…"}
            </p>
          ) : null}

          {/* Why-disabled hint. Without this the operator stares at a
              greyed-out button with no clue what's missing. Order:
              no brand → no platform → prompt too short. */}
          {!canStart && !start.isPending ? (
            <p
              style={{
                fontSize: 11,
                color: "var(--mu)",
                margin: "0 0 4px",
                lineHeight: 1.5,
              }}
            >
              {(() => {
                if (!brandId && !brand?.id) {
                  return tr ? "Önce bir marka seç." : "Pick a brand first.";
                }
                if (platforms.length === 0) {
                  return tr
                    ? "Bu marka için en az bir sosyal medya hesabı bağla (Hesaplar sekmesi)."
                    : "Connect at least one social account for this brand (Accounts tab).";
                }
                if (prompt.trim().length < 4) {
                  return tr
                    ? "En az 4 karakterlik bir konu / brief yaz."
                    : "Type at least 4 characters for the topic / brief.";
                }
                if (imageType === "graphical" && (imagePromptLoading || imagePrompt.trim().length === 0)) {
                  return tr
                    ? "Görsel istemi hazırlanıyor… hazır olunca üretebilirsin (istersen düzenle)."
                    : "Preparing the image prompt… you can generate once it's ready (edit it if you like).";
                }
                return tr ? "Hazırlanıyor…" : "Preparing…";
              })()}
            </p>
          ) : null}

          <button
            className="btn btn-primary"
            onClick={() => {
              // Guard against double-fire when mutation is already
              // running. React's `disabled` propagation lags one frame
              // behind the click on fast double-clicks; this is the
              // belt-and-suspenders check.
              if (start.isPending) return;
              start.mutate();
            }}
            disabled={!canStart}
            aria-busy={start.isPending}
            style={{
              width: "100%",
              justifyContent: "center",
              marginTop: "auto",
              flexDirection: "column",
              gap: 4,
              padding: "12px 22px",
              opacity: start.isPending ? 0.6 : 1,
              cursor: start.isPending ? "wait" : !canStart ? "not-allowed" : "pointer",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Animated indeterminate progress stripe — fills the
                button width while the synchronous /generations call is
                in flight (typically 6-15s). Without this the operator
                wonders if anything happened and click-spams. */}
            {start.isPending ? (
              <span
                aria-hidden
                style={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.22) 50%, transparent 100%)",
                  backgroundSize: "200% 100%",
                  animation: "shimmer 1.4s linear infinite",
                  pointerEvents: "none",
                }}
              />
            ) : null}
            <span style={{ display: "flex", alignItems: "center", gap: 8, position: "relative" }}>
              {start.isPending ? (
                <>
                  <Rosette size={14} stroke="currentColor" spin />
                  {tr
                    ? `Üretiyoruz · ~${Math.max(6, totalVariants * 3)} sn`
                    : `Generating · ~${Math.max(6, totalVariants * 3)}s`}
                </>
              ) : (
                <>
                  <Icons.Sparkles size={15} />
                  {tr
                    ? `Üret · ${totalVariants} varyant`
                    : `Generate · ${totalVariants} variants`}
                </>
              )}
            </span>
          </button>
        </div>

        {/* Right stream */}
        <div
          style={{
            overflow: "auto",
            padding: 24,
            background: "var(--bg)",
            position: "relative",
          }}
        >
          {!activeGenId ? (
            <GenerateEmpty brand={brand} tr={tr} />
          ) : (
            <VariantStream
              stream={stream}
              brand={brand}
              tr={tr}
              seeded={seededVersions}
              genId={activeGenId}
              genStatus={genStatus}
              postType={genPoll.data?.generation?.post_type ?? postType}
              onAppendVersion={(v) =>
                // Append the regenerated variant so the lane re-renders
                // with the new content immediately. v0.1.65.
                setSeededVersions((prev) => [...prev, v])
              }
              onCancel={() => {
                setActiveGenId(null);
                setSeededVersions([]);
              }}
            />
          )}
        </div>
      </div>
      {quotaBlock ? (
        <QuotaExceededModal
          q={quotaBlock}
          tr={tr}
          onClose={() => setQuotaBlock(null)}
          onUpgrade={() => { setQuotaBlock(null); nav({ to: "/app/billing" }); }}
          onTopup={() => { setQuotaBlock(null); nav({ to: "/app/billing", search: { tab: "credits" } as never }); }}
        />
      ) : null}
    </div>
  );
}

// ── Quota-exceeded modal — Phase 5 enforcement UI ─────────────────────────

function QuotaExceededModal({
  q,
  tr,
  onClose,
  onUpgrade,
  onTopup,
}: {
  q: { plan_slug?: string; used?: number; allowance?: number; remaining?: number; blocked_reason?: string; requested?: number };
  tr: boolean;
  onClose: () => void;
  onUpgrade: () => void;
  onTopup: () => void;
}) {
  const isTrialExpired = q.blocked_reason === "trial_expired";
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)",
        zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{
          width: "min(440px, 90vw)", padding: 28, background: "var(--surface)",
          boxShadow: "0 30px 80px rgba(0,0,0,0.5)",
        }}
      >
        <div className="eyebrow" style={{ marginBottom: 8, color: "var(--error)" }}>
          — {isTrialExpired ? (tr ? "Deneme bitti" : "Trial ended") : tr ? "Aylık limit doldu" : "Monthly limit reached"}
        </div>
        <h2 style={{ fontSize: 20, marginBottom: 8 }}>
          {isTrialExpired
            ? (tr ? "Plan seç ve üretmeye devam et." : "Pick a plan to keep generating.")
            : (tr ? "Bu ay için üretim hakkın bitti." : "You've used this month's generations.")}
        </h2>
        {!isTrialExpired && q.allowance ? (
          <p style={{ fontSize: 13, color: "var(--tx2)", marginBottom: 16 }}>
            {tr
              ? `${q.used ?? 0} / ${q.allowance} üretim kullandın. Bu istek için ${q.requested ?? 0} daha gerekiyor.`
              : `${q.used ?? 0} / ${q.allowance} generations used. This request needs ${q.requested ?? 0} more.`}
          </p>
        ) : (
          <p style={{ fontSize: 13, color: "var(--tx2)", marginBottom: 16 }}>
            {tr
              ? "Ücretsiz deneme bitti. Plan seçerek devam edebilirsin — taslakların kayıtlı kalır."
              : "Free trial has ended. Pick a paid plan to continue — your drafts are kept."}
          </p>
        )}
        <div style={{ display: "flex", gap: 8, flexDirection: "column" }}>
          <button className="btn btn-primary" onClick={onUpgrade} style={{ width: "100%", justifyContent: "center" }}>
            {tr ? "Plan yükselt →" : "Upgrade plan →"}
          </button>
          {!isTrialExpired ? (
            <button className="btn btn-secondary" onClick={onTopup} style={{ width: "100%", justifyContent: "center" }}>
              {tr ? "Ek kredi al" : "Buy top-up credits"}
            </button>
          ) : null}
          <button className="btn btn-ghost btn-sm" onClick={onClose}>
            {tr ? "Kapat" : "Close"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────

function GenerateEmpty({ brand, tr }: { brand: Brand | null; tr: boolean }) {
  return (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        padding: 48,
      }}
    >
      <Rosette size={120} stroke={brand?.accent_hex || "var(--accent)"} opacity={0.4} />
      <div className="eyebrow" style={{ marginTop: 32 }}>
        {tr ? "Üretmeye hazırız" : "Ready to generate"}
      </div>
      <h2
        style={{
          fontSize: 26,
          marginTop: 12,
          maxWidth: 480,
          textWrap: "balance" as never,
        }}
      >
        {tr
          ? "Varyantlar burada platform platform akacak."
          : "Variants will stream in here, platform by platform."}
      </h2>
      <p
        style={{
          color: "var(--tx2)",
          fontSize: 14,
          marginTop: 12,
          maxWidth: 440,
        }}
      >
        {tr ? (
          <>
            Konunu yaz, platformları seç ve{" "}
            <b style={{ color: "var(--tx)" }}>Üret</b>'e bas. Görsel önce, sonra
            metin — canlı göreceksin.
          </>
        ) : (
          <>
            Set your topic, pick platforms, then hit{" "}
            <b style={{ color: "var(--tx)" }}>Generate</b>. Image first, then caption —
            you'll see them appear in real time.
          </>
        )}
      </p>
    </div>
  );
}

// ── Stream container ──────────────────────────────────────────────────────

function VariantStream({
  stream,
  brand,
  tr,
  seeded,
  genId,
  genStatus,
  postType,
  onAppendVersion,
  onCancel,
}: {
  stream: ReturnType<typeof useGenerationStream>;
  brand: Brand | null;
  tr: boolean;
  seeded: SeededVersion[];
  genId: string | null;
  /** Server-side generation.status from the poll — authoritative over the WS. */
  genStatus?: string;
  /** Generation-wide post type ('feed'|'story') — per-platform EFFECTIVE
      type is derived in VariantCard (story only on IG/FB). v0.1.125. */
  postType?: string;
  onAppendVersion: (v: SeededVersion) => void;
  onCancel: () => void;
}) {
  // Merge stream lanes with seeded versions. WS-derived lane is the
  // source of truth (for live progress mid-flight); when a seeded
  // version exists for the same (platform, variant_number) we use it
  // to fill in fields the WS missed (the synchronous POST response
  // races ahead of the WS subscribe on fast generations).
  // 2026-08-06 — the WS lane may exist but be EMPTY and frozen mid-"streaming"
  // (socket dropped, tab slept, or frames were emitted before we subscribed —
  // nexus-stream is core NATS with no replay). Previously any seeded/polled
  // version for that key was FILTERED OUT because a WS lane existed, so a
  // fully-finished generation rendered as permanently-streaming empty lanes
  // while the server already had every variant. The server (poll) is the only
  // source guaranteed to converge, so let it FILL IN whatever the WS lane is
  // missing instead of losing to it.
  const seededByKey = new Map(seeded.map((v) => [`${v.platform}:${v.version_number}`, v]));
  const streamLanes = stream.laneOrder
    .map((k) => stream.lanes[k])
    .filter(Boolean)
    .map((l) => {
      const s = seededByKey.get(l.key);
      if (!s) return l;
      const imageURL = l.imageURL ?? s.image_url ?? undefined;
      const textBody = l.textBody ?? s.text_body ?? undefined;
      // Never downgrade a lane the WS already advanced; only promote it when
      // the server proves there's content the socket never delivered.
      const phase =
        l.phase === "done" || l.phase === "failed"
          ? l.phase
          : imageURL
            ? "done"
            : textBody
              ? "text_done"
              : l.phase;
      return {
        ...l,
        imageURL,
        textBody,
        hashtags: l.hashtags ?? s.hashtags ?? undefined,
        versionID: l.versionID ?? s.id,
        engine: l.engine ?? s.image_model,
        // The server row is the durable carrier of the image failure
        // reason; the WS frame is only a live shortcut. Either may be
        // absent — then the lane has nothing to explain.
        imageErrorKind: l.imageErrorKind ?? s.image_error_kind ?? undefined,
        imageError: l.imageError ?? s.image_error ?? undefined,
        phase,
      };
    });
  const haveStreamKey = new Set(streamLanes.map((l) => l.key));
  const seededLanes: VariantLane[] = seeded
    .filter((v) => !haveStreamKey.has(`${v.platform}:${v.version_number}`))
    .map((v) => ({
      key: `${v.platform}:${v.version_number}`,
      platform: v.platform,
      variantIndex: v.version_number,
      provider: v.provider,
      phase: v.image_url
        ? "done"
        : v.text_body
          ? "text_done"
          : "queued",
      imageURL: v.image_url ?? undefined,
      engine: v.image_model,
      textBody: v.text_body ?? undefined,
      hashtags: v.hashtags ?? undefined,
      versionID: v.id,
      imageErrorKind: v.image_error_kind ?? undefined,
      imageError: v.image_error ?? undefined,
    }));
  const lanes = [...streamLanes, ...seededLanes];
  // Group lanes by platform.
  const byPlatform = new Map<Platform, VariantLane[]>();
  for (const l of lanes) {
    const arr = byPlatform.get(l.platform) ?? [];
    arr.push(l);
    byPlatform.set(l.platform, arr);
  }

  // The server's generation.status wins over the socket's view: with the
  // async backend the WS can report "closed" (dropped frame, sleeping tab)
  // while the run is happily finishing, and it can report "complete" only
  // if it actually received gen.complete. Falling back to stream.status
  // keeps the live "streaming variants…" feel while the row is running.
  const effectiveStatus: typeof stream.status =
    genStatus === "ready" || genStatus === "approved"
      ? "complete"
      : genStatus === "failed"
        ? "failed"
        : genStatus === "pending" || genStatus === "running"
          ? stream.status === "complete" || stream.status === "failed"
            ? "open"
            : stream.status
          : // No server status yet (poll in flight, or it errored). A socket
            // that merely DROPPED must not be reported as "Generation failed"
            // — that scared operators into thinking finished work was lost.
            // Only the server may declare failure; a dead socket with content
            // already on screen is "complete", otherwise just "connecting".
            stream.status === "failed"
            ? lanes.some((l) => l.textBody || l.imageURL)
              ? "complete"
              : "connecting"
            : stream.status;

  const statusLabel =
    effectiveStatus === "connecting"
      ? tr
        ? "Bağlanıyor…"
        : "Connecting…"
      : effectiveStatus === "open"
        ? tr
          ? "Varyantlar akıyor…"
          : "Streaming variants…"
        : effectiveStatus === "complete"
          ? tr
            ? "Üretim tamamlandı"
            : "Generation complete"
          : effectiveStatus === "failed"
            ? tr
              ? `Üretim başarısız${stream.failedReason ? ` — ${stream.failedReason}` : ""}`
              : `Generation failed${stream.failedReason ? ` — ${stream.failedReason}` : ""}`
            : tr
              ? "Bağlantı kapatıldı"
              : "Connection closed";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "0 4px",
        }}
      >
        <div style={{ position: "relative", width: 24, height: 24 }}>
          <Rosette
            size={24}
            stroke={brand?.accent_hex || "var(--accent)"}
            spin={effectiveStatus === "open" || effectiveStatus === "connecting"}
            opacity={0.8}
          />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{statusLabel}</div>
          <div
            style={{ fontSize: 11, color: "var(--mu)", fontFamily: "var(--mono)" }}
          >
            {tr
              ? `${lanes.length} varyant · ${byPlatform.size} platform`
              : `${lanes.length} variants · ${byPlatform.size} platforms`}
          </div>
        </div>
        {effectiveStatus === "open" || effectiveStatus === "connecting" ? (
          <button className="btn btn-ghost btn-sm" onClick={onCancel}>
            <Icons.X size={12} /> {tr ? "İptal" : "Cancel"}
          </button>
        ) : null}
      </div>

      {lanes.length === 0 && stream.status !== "failed" ? (
        <div
          className="card"
          style={{ padding: 24, textAlign: "center", color: "var(--mu)", fontSize: 13 }}
        >
          {tr ? "Varyantlar hazırlanıyor, az kaldı…" : "Variants are being prepared…"}
        </div>
      ) : null}

      {Array.from(byPlatform.entries()).map(([platform, platformLanes]) => (
        <PlatformLane
          key={platform}
          platform={platform}
          lanes={platformLanes}
          postType={postType}
          brand={brand}
          tr={tr}
          genId={genId}
          onAppendVersion={onAppendVersion}
        />
      ))}
    </div>
  );
}

function PlatformLane({
  platform,
  lanes,
  brand,
  tr,
  genId,
  postType,
  onAppendVersion,
}: {
  platform: Platform;
  lanes: VariantLane[];
  brand: Brand | null;
  tr: boolean;
  genId: string | null;
  postType?: string;
  onAppendVersion: (v: SeededVersion) => void;
}) {
  const meta = PLATFORM_META[platform];
  return (
    <div className="card" style={{ padding: 18 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 14,
        }}
      >
        <PlatformChip platform={platform} size={28} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{meta.name}</div>
          <div className="mono" style={{ fontSize: 10, color: "var(--mu)" }}>
            {tr
              ? `${lanes.length} varyant`
              : `${lanes.length} variant${lanes.length > 1 ? "s" : ""}`}
          </div>
        </div>
        {lanes.some((l) => l.phase !== "done" && l.phase !== "failed") ? (
          <span className="badge badge-blue">
            <span className="dot pulse-dot" />
            {tr ? "Akıyor" : "Streaming"}
          </span>
        ) : (
          <span className="badge badge-green">
            <span className="dot" />
            {tr ? "Hazır" : "Ready"}
          </span>
        )}
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            lanes.length === 1 ? "1fr" : "repeat(auto-fill, minmax(280px, 1fr))",
          gap: 12,
        }}
      >
        {lanes.map((l) => (
          <VariantCard key={l.key} lane={l} brand={brand} tr={tr} genId={genId} platform={platform} postType={postType} onNewVariant={onAppendVersion} />
        ))}
      </div>
    </div>
  );
}

function VariantCard({
  lane,
  brand,
  tr,
  genId,
  platform,
  postType,
  onNewVariant,
}: {
  lane: VariantLane;
  brand: Brand | null;
  tr: boolean;
  genId: string | null;
  postType?: string;
  platform: Platform;
  onNewVariant: (v: SeededVersion) => void;
}) {
  const qc = useQueryClient();
  // ApproveOpen — when true, render the schedule/draft chooser
  // overlay on top of the card instead of immediately materializing
  // a post. Without this the operator can't pick when to publish.
  const [approveOpen, setApproveOpen] = useState(false);
  const [scheduleAt, setScheduleAt] = useState(() => {
    // Default to the next 15-min slot at least 15 min from now —
    // same shape PostDrawer uses.
    const d = new Date();
    d.setSeconds(0, 0);
    d.setMinutes(d.getMinutes() + 15);
    const m = d.getMinutes();
    const rounded = Math.ceil(m / 15) * 15;
    if (rounded === 60) {
      d.setHours(d.getHours() + 1);
      d.setMinutes(0);
    } else {
      d.setMinutes(rounded);
    }
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });

  // Approve uses /generations/:genId/approve which materializes a
  // content.posts row. /variants/:id/accept was wrong here — it only
  // flipped version.status and never produced a publishable post,
  // leaving the operator wondering where their post went.
  const approve = useMutation({
    mutationFn: (mode: "schedule" | "draft") => {
      if (!genId || !lane.versionID) {
        throw new Error("missing generation or version id");
      }
      const body: Record<string, unknown> = {
        selections: { [platform]: lane.versionID },
      };
      if (mode === "draft") {
        body.save_as_draft = true;
      } else {
        body.scheduled_for = new Date(scheduleAt).toISOString();
      }
      return api(`/generations/${genId}/approve`, { method: "POST", body });
    },
    onSuccess: () => {
      setApproveOpen(false);
      void qc.invalidateQueries();
      // Stay on Generate so the operator can approve the other platform
      // lanes in this generation. The lane re-renders to its post-approve
      // state via cache invalidation; sidebar "Gönderiler" is one click
      // away when they're done with all platforms.
    },
  });
  const reject = useMutation({
    mutationFn: () =>
      lane.versionID
        // /versions/, NOT /variants/ — the latter is registered in neither
        // the gateway nor content-service, so the Reject button 404'd on
        // every click. Same bug the regenerate comment below describes;
        // this one was missed. Real route: content-service
        // internal/server/server.go "POST /v1/versions/{id}/reject".
        ? api(`/versions/${lane.versionID}/reject`, { method: "POST" })
        : Promise.resolve(),
    onSuccess: () => qc.invalidateQueries(),
  });
  // Regenerate calls POST /v1/generations/{genId}/regenerate (NOT
  // /variants/:id/regenerate which doesn't exist and was silently
  // 404-ing — that's why earlier the button looked like it did
  // nothing). Backend returns the new version; we append it to the
  // local lane store and the UI re-renders. v0.1.65.
  // Scope="both" — regen both caption + image. For graphical, this
  // also rotates the engine (GPT-Image → Nano Banana → ...).
  const regen = useMutation({
    mutationFn: () => {
      if (!genId) {
        throw new Error("missing generation id");
      }
      return api<SeededVersion>(
        `/generations/${genId}/regenerate`,
        {
          method: "POST",
          body: { platform, scope: "both" },
        },
      );
    },
    onSuccess: (newVer) => {
      // Prefer to merge the new variant into the stream lane store
      // so the card flips to the new content immediately. Fall back
      // to a full refetch if the parent doesn't expose mergers.
      if (newVer && onNewVariant) {
        onNewVariant(newVer);
      } else {
        void qc.invalidateQueries({ queryKey: ["generation", genId] });
        void qc.invalidateQueries({ queryKey: ["versions", genId] });
      }
    },
  });

  const accent = brand?.accent_hex || "var(--accent)";
  // null whenever the backend sent no reason (older rows, or the sibling
  // field simply isn't there) — the card then behaves exactly as before.
  const imgFail = lane.imageURL ? null : imageFailure(lane);
  // A lane that is still "queued" but already carries a failure reason has
  // nothing more coming for the image — stop shimmering and say why.
  const showShimmer = lane.phase === "queued" && !imgFail;
  const showImageWriting = lane.phase === "image_ready" && !lane.textBody;

  return (
    <div
      className="card"
      style={{
        padding: 0,
        overflow: "hidden",
        borderColor: lane.phase === "failed" ? "var(--error)" : "var(--b2)",
        position: "relative",
      }}
    >
      {/* Regenerating overlay — appears across the whole card while
          the regen call is in flight. Without this the operator
          presses ↻ and sees nothing change (the new variant takes
          6-30s server-side). v0.1.65. */}
      {regen.isPending ? (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(0,0,0,0.7)",
            backdropFilter: "blur(2px)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            zIndex: 3,
            color: "var(--tx)",
            fontSize: 12,
          }}
        >
          <Rosette size={28} stroke="var(--accent)" spin />
          <span>{tr ? "Yeniden üretiliyor…" : "Regenerating…"}</span>
          <span style={{ fontSize: 10, color: "var(--mu)", fontFamily: "var(--mono)" }}>
            {tr ? "~10-30 sn" : "~10-30s"}
          </span>
        </div>
      ) : null}
      {regen.isError ? (
        <div
          style={{
            position: "absolute",
            top: 4,
            left: 4,
            right: 4,
            padding: "4px 8px",
            background: "rgba(239,68,68,0.92)",
            color: "#fff",
            fontSize: 11,
            borderRadius: 4,
            zIndex: 3,
          }}
        >
          {tr ? "Hata: " : "Error: "}
          {regen.error instanceof Error ? regen.error.message : "regenerate failed"}
        </div>
      ) : null}
      <div
        style={{
          // Match the real rendered canvas so the preview isn't cropped.
          // IG is 4:5 portrait (v0.3.33), X 16:9, FB/LI landscape.
          // v0.1.125 — per-platform EFFECTIVE post type: story renders a
          // 9:16 canvas on IG/FB only; every other platform of a story
          // generation renders (and previews) its normal feed ratio.
          aspectRatio:
            postType === "story" && (platform === "instagram" || platform === "facebook")
              ? "9/16"
              : platform === "instagram"
                ? "4/5"
                : platform === "x"
                  ? "16/9"
                  : platform === "facebook" || platform === "linkedin"
                    ? "1.91/1"
                    : "1",
          position: "relative",
          overflow: "hidden",
          background: "var(--elev)",
        }}
      >
        {showShimmer ? (
          <div className="skel" style={{ position: "absolute", inset: 0 }} />
        ) : lane.imageURL ? (
          <img
            src={resolveImageUrl(lane.imageURL)}
            alt=""
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              // contain so landscape/portrait diffusion outputs fit
              // fully inside the square lane (cover crops). v0.1.63.
              objectFit: "contain",
              background: "#0a0a0a",
            }}
          />
        ) : (
          <>
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: `linear-gradient(135deg, ${accent}40 0%, #0a0a0a 50%, ${accent}20 100%)`,
              }}
            />
            {/* Renders nothing at all when the backend sent no reason, so a
                plain missing image still looks exactly like it used to. */}
            <ImageFailureNotice failure={imgFail} tr={tr} overlay />
          </>
        )}
        <div
          style={{
            position: "absolute",
            top: 8,
            left: 8,
            fontFamily: "var(--mono)",
            fontSize: 9,
            color: "#fff",
            background: "rgba(0,0,0,0.5)",
            padding: "2px 6px",
            borderRadius: 3,
            letterSpacing: "0.1em",
          }}
        >
          v{lane.variantIndex} · {PLATFORM_META[lane.platform].name}
        </div>
      </div>

      <div style={{ padding: 14, minHeight: 140 }}>
        {showShimmer ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <Skeleton h={10} />
            <Skeleton h={10} w="80%" />
            <Skeleton h={10} w="60%" />
          </div>
        ) : showImageWriting ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 11,
              color: "var(--mu)",
            }}
          >
            <Rosette size={14} stroke="var(--accent)" spin />{" "}
            {tr ? "Metin yazılıyor…" : "Writing caption…"}
          </div>
        ) : lane.textBody ? (
          <>
            <div
              style={{
                fontSize: 12.5,
                lineHeight: 1.55,
                whiteSpace: "pre-wrap",
                color: "var(--tx)",
              }}
            >
              {lane.textBody}
            </div>
            {lane.hashtags && lane.hashtags.length > 0 ? (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 4,
                  marginTop: 8,
                }}
              >
                {lane.hashtags.map((h) => (
                  <span key={h} style={{ fontSize: 11, color: accent }}>
                    #{h}
                  </span>
                ))}
              </div>
            ) : null}
          </>
        ) : null}
      </div>

      {lane.phase === "done" ? (
        <div
          style={{
            borderTop: "1px solid var(--b1)",
            padding: "8px 12px",
            display: "flex",
            gap: 6,
            alignItems: "center",
          }}
        >
          <button
            onClick={() => reject.mutate()}
            disabled={reject.isPending}
            className="btn btn-ghost btn-sm"
            style={{ padding: "4px 8px" }}
            title={tr ? "Reddet" : "Reject"}
          >
            <Icons.X size={13} />
          </button>
          <button
            className="btn btn-ghost btn-sm"
            style={{ padding: "4px 8px" }}
            title={tr ? "Düzenle" : "Edit"}
          >
            <Icons.Edit size={13} />
          </button>
          <button
            onClick={() => regen.mutate()}
            disabled={regen.isPending}
            className="btn btn-ghost btn-sm"
            style={{ padding: "4px 8px" }}
            title={tr ? "Yeniden üret" : "Regenerate"}
          >
            <Icons.Refresh size={13} />
          </button>
          <div style={{ flex: 1 }} />
          <button
            onClick={() => setApproveOpen(true)}
            disabled={!genId || !lane.versionID}
            className="btn btn-primary btn-sm"
            style={{ padding: "5px 10px" }}
          >
            <Icons.Check size={12} /> {tr ? "Onayla" : "Approve"}
          </button>
        </div>
      ) : null}

      {approveOpen ? (
        <ApproveOverlay
          tr={tr}
          scheduleAt={scheduleAt}
          setScheduleAt={setScheduleAt}
          isPending={approve.isPending}
          errMsg={approve.error instanceof Error ? approve.error.message : null}
          onClose={() => setApproveOpen(false)}
          onSchedule={() => approve.mutate("schedule")}
          onDraft={() => approve.mutate("draft")}
        />
      ) : null}
    </div>
  );
}

// ApproveOverlay — small inline chooser rendered on top of the
// variant card. Operator picks one of two outcomes:
//   1. Schedule for a specific date + time (creates Post with
//      status=scheduled, published by the worker at that time)
//   2. Save as draft (creates Post with status=draft, no auto-publish;
//      operator can edit + manually publish later from Posts page)
// Used to be a single "Approve" button that quietly flipped a version
// status; operators couldn't tell what happened and didn't get a
// schedule choice.
function ApproveOverlay({
  tr,
  scheduleAt,
  setScheduleAt,
  isPending,
  errMsg,
  onClose,
  onSchedule,
  onDraft,
}: {
  tr: boolean;
  scheduleAt: string;
  setScheduleAt: (v: string) => void;
  isPending: boolean;
  errMsg: string | null;
  onClose: () => void;
  onSchedule: () => void;
  onDraft: () => void;
}) {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(0,0,0,0.7)",
        backdropFilter: "blur(2px)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        padding: 16,
        gap: 10,
        zIndex: 2,
      }}
    >
      <div className="mono" style={{ fontSize: 9, letterSpacing: "0.16em", color: "var(--mu)", textTransform: "uppercase" }}>
        {tr ? "Bu varyantı onayla" : "Approve this variant"}
      </div>
      <label style={{ fontSize: 11, color: "var(--tx2)" }}>
        {tr ? "Yayın zamanı" : "Publish at"}
        <input
          type="datetime-local"
          value={scheduleAt}
          onChange={(e) => setScheduleAt(e.target.value)}
          disabled={isPending}
          className="input"
          style={{ width: "100%", marginTop: 4, height: 32, padding: "0 8px", fontSize: 12 }}
        />
      </label>
      {errMsg ? (
        <div style={{ fontSize: 11, color: "var(--error)" }}>{errMsg}</div>
      ) : null}
      <div style={{ display: "flex", gap: 6 }}>
        <button
          onClick={onSchedule}
          disabled={isPending || !scheduleAt}
          className="btn btn-primary btn-sm"
          style={{ flex: 1, justifyContent: "center" }}
        >
          {isPending ? (tr ? "Kaydediliyor…" : "Saving…") : (tr ? "Planla" : "Schedule")}
        </button>
        <button
          onClick={onDraft}
          disabled={isPending}
          className="btn btn-secondary btn-sm"
          style={{ flex: 1, justifyContent: "center" }}
        >
          {tr ? "Taslak" : "Draft"}
        </button>
      </div>
      <button
        onClick={onClose}
        disabled={isPending}
        className="btn btn-ghost btn-sm"
        style={{ justifyContent: "center" }}
      >
        {tr ? "İptal" : "Cancel"}
      </button>
    </div>
  );
}
